import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";
import { NAV } from "../../i18n/content";
import { LanguageSwitcher } from "../LanguageSwitcher";

export function Header() {
  const { t, pathFor } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const onDark = (location.pathname === "/" || location.pathname === "/es") && !scrolled;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-md border-b border-border"
          : "bg-gradient-to-b from-charcoal/40 to-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to={pathFor("/")} className="group flex flex-col leading-none" data-testid="logo-link">
          <span className={`font-serif text-2xl font-medium tracking-tight transition-colors duration-300 ${onDark ? "text-cream" : "text-charcoal"}`}>Shakti Love</span>
          <span className={`text-[10px] font-semibold uppercase tracking-overline transition-colors duration-300 ${onDark ? "text-gold" : "text-terracotta"}`}>Music · DEEJAY</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.path}
              to={pathFor(item.path)}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-300 ${
                  onDark
                    ? isActive
                      ? "text-gold"
                      : "text-cream/90 hover:text-gold"
                    : isActive
                      ? "text-terracotta"
                      : "text-charcoal/80 hover:text-terracotta"
                }`
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LanguageSwitcher />
          <Link
            to={pathFor("/contact")}
            data-testid="header-book-btn"
            className="rounded-full bg-terracotta px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terracotta-deep"
          >
            {t("cta.bookShort")}
          </Link>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSwitcher testId="language-switcher-mobile" />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
            className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-300 ${onDark ? "text-cream" : "text-charcoal"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden border-t border-border bg-cream"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-6" aria-label="Mobile">
              {NAV.map((item, i) => (
                <NavLink
                  key={item.path}
                  to={pathFor(item.path)}
                  data-testid={`mobile-nav-${item.key}`}
                  className={({ isActive }) =>
                    `border-b border-border py-4 font-serif text-3xl transition-colors ${
                      isActive ? "text-terracotta" : "text-charcoal"
                    }`
                  }
                >
                  <span className="mr-3 text-xs font-sans text-terracotta">0{i + 1}</span>
                  {t(`nav.${item.key}`)}
                </NavLink>
              ))}
              <Link
                to={pathFor("/contact")}
                data-testid="mobile-book-btn"
                className="mt-6 rounded-full bg-terracotta px-6 py-4 text-center text-sm font-semibold text-white"
              >
                {t("cta.book")}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
